import type { QueryClient } from "@tanstack/vue-query";
import type { UserNotificationChangeMessage, UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import { disableMercure, useMercure } from "@verbleif/lib/src/composables/Mercure/useMercure";
import { patchHydraData, removeHydraQueryItem } from "@verbleif/lib/src/composables/Query/hydraCacheMutators";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { createGlobalState, tryOnScopeDispose } from "@vueuse/core";
import { computed, watch } from "vue";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { useConfigStore } from "../../ConfigStore/useConfigStore";
import { resourceKeys } from "../../Query/resourceKeys";
import { useMercureState } from "../../useMercureState";
import { useNotificationsV2 } from "../useNotificationsV2";
import { refreshUserNotificationUnreadCount, userNotificationUnreadCountQueryKey } from "./notifications.api";

/**
 * The user's private Mercure topic for notifications v2 (the API publishes on `/api/users/{id}/user_notifications`;
 * useMercure adds the `/api` prefix). The only place the topic is spelled out.
 */
export function userNotificationsMercureTopic(userId: number): string {
  return `/users/${userId}/user_notifications`;
}

const notificationListsKey = [...resourceKeys.notifications.root, "list"] as const;

function isUnreadCount(value: unknown): value is UserNotificationChangeMessage["unreadCount"] {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<UserNotificationChangeMessage["unreadCount"]>;
  return typeof candidate.total === "number"
    && Array.isArray(candidate.locations)
    && candidate.locations.every(entry => typeof entry?.location === "string" && typeof entry.count === "number");
}

export function isUserNotificationChangeMessage(value: unknown): value is UserNotificationChangeMessage {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<UserNotificationChangeMessage>;
  return typeof candidate.type === "string"
    && (candidate.notification === null || typeof candidate.notification === "string")
    && isUnreadCount(candidate.unreadCount);
}

function cachedItemIsUnread(data: unknown, iri: string): boolean {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const pages = "pages" in data && Array.isArray(data.pages) ? data.pages : [data];
  return pages.some((page: unknown) => {
    if (typeof page !== "object" || page === null || !("member" in page) || !Array.isArray(page.member)) {
      return false;
    }
    return page.member.some((item: Partial<UserNotificationHydraItem>) => item["@id"] === iri && !item.readAt);
  });
}

/**
 * Applies one change of the user's notifications to the cache: the unread count comes along with every update; a
 * single read or hidden item is patched in the cached inbox lists, anything else (a new item, a restored item, mark
 * all read, a type this client does not know yet) refetches the lists that are on screen.
 */
export function applyUserNotificationChange(client: QueryClient, userId: number, message: UserNotificationChangeMessage): void {
  client.setQueryData(userNotificationUnreadCountQueryKey(userId), {
    total: message.unreadCount.total,
    locations: message.unreadCount.locations,
  });

  const iri = message.notification;
  if (message.type === "read" && iri) {
    const readAt = new Date().toISOString();
    for (const [key, data] of client.getQueriesData({ queryKey: notificationListsKey })) {
      if (cachedItemIsUnread(data, iri)) {
        client.setQueryData(key, patchHydraData<UserNotificationHydraItem>(data, iri, { readAt }));
      }
    }
    return;
  }

  if (message.type === "hidden" && iri) {
    removeHydraQueryItem(client, notificationListsKey, iri);
    return;
  }

  void client.invalidateQueries({ queryKey: notificationListsKey });
}

/**
 * Follows the signed-in user's notifications live while notifications v2 is on: the navbar count and the open inbox
 * lists update from the Mercure updates of the API instead of being refetched after a push.
 */
export const useUserNotificationsLive = createGlobalState(() => {
  const authStore = useAuthStore();
  const configStore = useConfigStore();
  const mercureState = useMercureState();
  const { isV2Enabled } = useNotificationsV2();

  const followedUserId = computed<number | null>(() => {
    const userId = authStore.user.value?.id;
    return isV2Enabled.value && userId ? userId : null;
  });

  function onMessage(message: unknown): void {
    const userId = followedUserId.value;
    if (userId === null || !isUserNotificationChangeMessage(message)) {
      return;
    }
    applyUserNotificationChange(getQueryClient(), userId, message);
  }

  const mercure = useMercure<UserNotificationChangeMessage>({
    hubUrlGetter: () => configStore.config.value?.mercureUrl || "",
    mercureTokenGetter: () => mercureState.mercureToken.value || "",
    refreshMercureToken: mercureState.refreshMercureToken,
    // Global state outlives the component that first asked for it; the watch below opens and closes the socket.
    closeOnUnmount: false,
    onAddOrUpdate: onMessage,
    onDelete: onMessage,
  });

  /**
   * True only while the stream of the followed user is open. While it is down (hub unreachable, token refresh
   * failing, Mercure disabled) updates are missed, so callers refresh the count and lists themselves.
   */
  const isLive = computed(() => followedUserId.value !== null && !disableMercure.value && mercure.isOpen.value);

  /** Reloads the unread count and the open inbox lists from the API. */
  function resync(): Promise<void> {
    const client = getQueryClient();
    return Promise.all([
      refreshUserNotificationUnreadCount(),
      client.invalidateQueries({ queryKey: notificationListsKey }),
    ]).then(() => undefined);
  }

  /** After a change of this tab (read, hide, mark all read): the Mercure update brings the count when live. */
  function refreshCountUnlessLive(): Promise<void> {
    return isLive.value ? Promise.resolve() : refreshUserNotificationUnreadCount();
  }

  // Updates may have been missed before an open that follows a failure: a reopen, or a first open after the stream
  // or the token refresh failed from the start. Such an open catches up once; a clean first open does not.
  let openedBeforeForUser: number | null = null;
  let failuresSeen = 0;
  let tokenRefreshFailed = false;
  watch(() => mercure.isOpen.value, (open) => {
    const userId = followedUserId.value;
    if (!open || userId === null) {
      return;
    }
    if (openedBeforeForUser === userId || mercure.failures.value > failuresSeen || tokenRefreshFailed) {
      void resync();
    }
    openedBeforeForUser = userId;
    failuresSeen = mercure.failures.value;
    tokenRefreshFailed = false;
  });

  // Each subscription run gets an id, so a token refresh that fails after a newer run started is ignored.
  let runId = 0;
  watch([followedUserId, disableMercure], async ([userId, disabled]) => {
    const run = ++runId;
    mercure.close();
    openedBeforeForUser = null;
    failuresSeen = mercure.failures.value;
    tokenRefreshFailed = false;
    if (userId === null || disabled) {
      return;
    }
    // A private topic is only delivered to a token that lists it; a token cached from before may not.
    try {
      await mercureState.refreshMercureToken();
    } catch (error) {
      // useMercure fetches a token itself when it has none and retries on a 401, so connect anyway.
      console.debug("[useUserNotificationsLive] Mercure token refresh failed", error);
      if (run === runId) {
        tokenRefreshFailed = true;
      }
    }
    if (run !== runId || followedUserId.value !== userId || disableMercure.value) {
      return;
    }
    await mercure.connect(userNotificationsMercureTopic(userId));
  }, { immediate: true });

  tryOnScopeDispose(() => {
    mercure.close();
  });

  return { isLive, resync, refreshCountUnlessLive };
});
